class MbGiftPackaging extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleNoteInput = debounce(this.handleNoteInput.bind(this), 600);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleChange);
    this.querySelector('[data-mb-gift-note]')?.addEventListener('input', this.handleNoteInput);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.querySelector('[data-mb-gift-note]')?.removeEventListener('input', this.handleNoteInput);
  }

  get paidVariantIds() {
    return (this.dataset.paidVariantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  get selectedInput() {
    return this.querySelector('input[type="radio"]:checked');
  }

  get noteInput() {
    return this.querySelector('[data-mb-gift-note]');
  }

  handleChange(event) {
    if (!event.target.matches('input[type="radio"]')) return;
    this.applySelection();
  }

  handleNoteInput() {
    this.applySelection({ noteOnly: true });
  }

  async applySelection(options = {}) {
    const selected = this.selectedInput;
    if (!selected || this.busy) return;

    const selectedVariantId = selected.dataset.variantId || '';
    const selectedTier = selected.dataset.tier || 'free';
    const selectedLabel = selected.dataset.label || 'Signature gift box';
    const note = this.noteInput?.value || '';

    if (selected.disabled) return;

    this.setBusy(true);

    try {
      const cart = await this.fetchCart();
      const paidVariantIds = this.paidVariantIds;
      const updates = {};
      const existingSelectedLine = cart.items.find((item) => String(item.variant_id) === selectedVariantId);

      cart.items.forEach((item) => {
        const itemVariantId = String(item.variant_id);
        if (!paidVariantIds.includes(itemVariantId)) return;

        if (!selectedVariantId || itemVariantId !== selectedVariantId) {
          updates[item.key] = 0;
          return;
        }

        if (item.quantity !== 1) {
          updates[item.key] = 1;
        }
      });

      const attributes = {
        'Gift packaging': selectedLabel,
        'Gift packaging tier': selectedTier,
        'Gift note': note,
      };

      if (Object.keys(updates).length > 0 || options.noteOnly || selectedTier === 'free') {
        await this.updateCart({ updates, attributes });
      } else {
        await this.updateCart({ attributes });
      }

      if (selectedVariantId && !existingSelectedLine) {
        await this.addGiftVariant(selectedVariantId, selectedLabel, selectedTier);
      }

      await this.refreshCartSections();
      this.setStatus('Packaging updated');
    } catch (error) {
      console.error(error);
      this.setStatus('Packaging could not be updated');
    } finally {
      this.setBusy(false);
    }
  }

  fetchCart() {
    return fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  updateCart(payload) {
    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    }).then((response) => response.json());
  }

  addGiftVariant(variantId, label, tier) {
    return fetch(`${routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              '_MANBADI gift packaging': label,
              '_MANBADI gift tier': tier,
            },
          },
        ],
      }),
    }).then((response) => response.json());
  }

  async refreshCartSections() {
    const sections = ['cart-icon-bubble'];

    if (document.querySelector('cart-drawer')) {
      sections.push('cart-drawer');
    }

    if (document.getElementById('main-cart-items')) {
      sections.push('main-cart-items');
    }

    if (document.getElementById('main-cart-footer')) {
      sections.push('main-cart-footer');
    }

    const response = await fetch(
      `${routes.cart_url}?sections=${sections.join(',')}&sections_url=${window.location.pathname}`,
      { credentials: 'same-origin' }
    );
    const parsedSections = await response.json();
    const parser = new DOMParser();

    if (parsedSections['cart-icon-bubble']) {
      const source = parser
        .parseFromString(parsedSections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      const target =
        document.getElementById('cart-icon-bubble')?.closest('.shopify-section') ||
        document.getElementById('shopify-section-cart-icon-bubble');
      if (source && target) target.replaceWith(source);
    }

    if (parsedSections['cart-drawer']) {
      const source = parser
        .parseFromString(parsedSections['cart-drawer'], 'text/html')
        .querySelector('#CartDrawer .drawer__inner');
      const target = document.querySelector('#CartDrawer .drawer__inner');
      if (source && target) target.replaceWith(source);
    }

    if (parsedSections['main-cart-items']) {
      const source = parser
        .parseFromString(parsedSections['main-cart-items'], 'text/html')
        .querySelector('#main-cart-items .js-contents');
      const target = document.querySelector('#main-cart-items .js-contents');
      if (source && target) target.replaceWith(source);
    }

    if (parsedSections['main-cart-footer']) {
      const source = parser
        .parseFromString(parsedSections['main-cart-footer'], 'text/html')
        .querySelector('#main-cart-footer');
      const target = document.getElementById('main-cart-footer');
      if (source && target) target.replaceWith(source);
    }
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.classList.toggle('is-updating', isBusy);
    this.querySelectorAll('input, textarea').forEach((control) => {
      if (!isBusy && control.hasAttribute('data-mb-originally-disabled')) {
        control.removeAttribute('data-mb-originally-disabled');
        return;
      }

      if (isBusy && control.disabled) {
        control.setAttribute('data-mb-originally-disabled', 'true');
        return;
      } else {
        control.disabled = isBusy;
      }
    });
  }

  setStatus(message) {
    const status = this.querySelector('[data-mb-gift-status]');
    if (!status) return;
    status.textContent = message;
    window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 1800);
  }
}

if (!customElements.get('mb-gift-packaging')) {
  customElements.define('mb-gift-packaging', MbGiftPackaging);
}
